// v2 butterfly — a proper insect: a segmented body (head, thorax, long
// abdomen), thin clubbed antennae, and two wing pairs per side — a large
// forewing and a smaller hindwing with a slight dihedral — each carrying a
// contrasting spot. Six deterministic wing palettes (monarch, swallowtail,
// morpho, rose, cabbage white, copper). Ported 1:1 from age-of-mcp's
// v2/butterfly.ts.
import * as THREE from "three";
import { defineModel, type Part } from "../../lib";

const WINGS = [
  { fore: "#e88f2a", hind: "#d97f22", spot: "#2a2018" },
  { fore: "#e8d84e", hind: "#d8c83e", spot: "#2a2820" },
  { fore: "#4a8ae0", hind: "#3a6ec4", spot: "#dce8f8" },
  { fore: "#f4a3c4", hind: "#e08aae", spot: "#f8e8f0" },
  { fore: "#f0f0e2", hind: "#dcdccc", spot: "#3a3a30" },
  { fore: "#c86a32", hind: "#b05a28", spot: "#f0d8a8" },
];

const BODY = "#2a2218";

function butterflyParts(v: number): Part[] {
  const w = WINGS[v % WINGS.length]!;
  const parts: Part[] = [
    // thorax, long abdomen and head — a real segmented insect body
    { geometry: new THREE.IcosahedronGeometry(0.007, 0), color: BODY,
      position: [0.004, 0.02, 0] },
    { geometry: new THREE.IcosahedronGeometry(0.0055, 0), color: BODY,
      position: [-0.01, 0.019, 0], scale: [2.0, 0.9, 0.9] },
    { geometry: new THREE.IcosahedronGeometry(0.005, 0), color: BODY,
      position: [0.013, 0.021, 0] },
    // clubbed antennae
    { geometry: new THREE.CylinderGeometry(0.0012, 0.0012, 0.012, 3), color: BODY,
      position: [0.018, 0.025, 0.0035], rotation: [0.25, 0, -0.65] },
    { geometry: new THREE.CylinderGeometry(0.0012, 0.0012, 0.012, 3), color: BODY,
      position: [0.018, 0.025, -0.0035], rotation: [-0.25, 0, -0.65] },
    // forewings — large, swept slightly forward, gentle dihedral
    { geometry: new THREE.IcosahedronGeometry(0.0205, 0), color: w.fore,
      position: [0.005, 0.0215, 0.0235], scale: [1.3, 0.13, 1.4],
      rotation: [-0.12, 0.2, 0] },
    { geometry: new THREE.IcosahedronGeometry(0.0205, 0), color: w.fore,
      position: [0.005, 0.0215, -0.0235], scale: [1.3, 0.13, 1.4],
      rotation: [0.12, -0.2, 0] },
    // hindwings — smaller, tucked behind
    { geometry: new THREE.IcosahedronGeometry(0.0145, 0), color: w.hind,
      position: [-0.013, 0.0205, 0.0175], scale: [1.15, 0.13, 1.2],
      rotation: [-0.1, -0.15, 0] },
    { geometry: new THREE.IcosahedronGeometry(0.0145, 0), color: w.hind,
      position: [-0.013, 0.0205, -0.0175], scale: [1.15, 0.13, 1.2],
      rotation: [0.1, 0.15, 0] },
    // a contrasting spot on each forewing
    { geometry: new THREE.IcosahedronGeometry(0.005, 0), color: w.spot,
      position: [0.008, 0.0242, 0.026], scale: [1.2, 0.18, 1.2],
      rotation: [-0.12, 0.2, 0] },
    { geometry: new THREE.IcosahedronGeometry(0.005, 0), color: w.spot,
      position: [0.008, 0.0242, -0.026], scale: [1.2, 0.18, 1.2],
      rotation: [0.12, -0.2, 0] },
  ];
  return parts;
}

export const butterflyV2 = defineModel({
  id: "butterfly-v2",
  batchId: "fable",
  name: "Butterfly (v2)",
  tags: ["creature","butterfly","low-poly","age-of-mcp","fable"],
  summary: "Low-poly butterfly.",
  description: "Ported from the age-of-mcp gallery, v2 style.",
  author: "age-of-mcp",
  footprint: 0.05,
  variants: [
    { id: "variant-0", label: "Variant 1", parts: () => butterflyParts(0) },
    { id: "variant-1", label: "Variant 2", parts: () => butterflyParts(1) },
    { id: "variant-2", label: "Variant 3", parts: () => butterflyParts(2) },
    { id: "variant-3", label: "Variant 4", parts: () => butterflyParts(3) },
    { id: "variant-4", label: "Variant 5", parts: () => butterflyParts(4) },
    { id: "variant-5", label: "Variant 6", parts: () => butterflyParts(5) },
  ],
  animations: [],
});
